import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import { 
  Users, Plus, Search, Loader2, MoreHorizontal, 
  Key, KeyRound, RefreshCw, Copy, Ban, CheckCircle, 
  Shield, UserX, UserCheck, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const UsuariosPage = () => {
  const { api } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [senhaDialogOpen, setSenhaDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [usuarioToDelete, setUsuarioToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [senhaTemporaria, setSenhaTemporaria] = useState('');
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    nome: '',
    password: '',
    perfil: 'administrador_empresa',
    empresa_id: ''
  });

  const perfis = [
    { value: 'administrador_plataforma', label: 'Admin Plataforma' },
    { value: 'administrador_empresa', label: 'Admin Empresa' },
    { value: 'rh', label: 'RH' },
    { value: 'gestor', label: 'Gestor' },
    { value: 'lider', label: 'Líder' },
    { value: 'encarregado', label: 'Encarregado' },
    { value: 'empregado', label: 'Empregado' },
    { value: 'implantador', label: 'Implantador' }
  ];

  useEffect(() => {
    fetchUsuarios();
    fetchEmpresas();
  }, []);

  const fetchUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      setUsuarios(response.data);
    } catch (error) {
      console.error('Error fetching usuarios:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error fetching empresas:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/usuarios', formData);
      toast.success('Usuário criado com sucesso!');
      setDialogOpen(false);
      setFormData({ email: '', nome: '', password: '', perfil: 'administrador_empresa', empresa_id: '' });
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleResetarSenha = async (usuario) => {
    try {
      const response = await api.post(`/usuarios/${usuario.id}/resetar-senha`);
      
      if (response.data.senha_temporaria) {
        setUsuarioSelecionado(usuario);
        setSenhaTemporaria(response.data.senha_temporaria);
        setSenhaDialogOpen(true);
      }
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  const handleForcarTrocaSenha = async (usuario) => {
    try {
      await api.put(`/usuarios/${usuario.id}/forcar-troca-senha?forcar=true`);
      toast.success(`${usuario.nome} deverá trocar a senha no próximo login`);
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao marcar troca de senha');
    }
  };

  const handleBloquearUsuario = async (usuario, bloquear) => {
    try {
      await api.put(`/usuarios/${usuario.id}/bloquear?bloquear=${bloquear}`);
      toast.success(`Usuário ${usuario.nome} ${bloquear ? 'bloqueado' : 'desbloqueado'} com sucesso`);
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status do usuário');
    }
  };

  const handleDeleteClick = (usuario) => {
    setUsuarioToDelete(usuario);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!usuarioToDelete) return;
    
    setDeleting(true);
    try {
      const response = await api.delete(`/usuarios/${usuarioToDelete.id}`);
      toast.success(response.data.message || 'Usuário excluído com sucesso!');
      setDeleteDialogOpen(false);
      setUsuarioToDelete(null);
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setDeleting(false);
    }
  };

  const copiarSenha = () => {
    navigator.clipboard.writeText(senhaTemporaria);
    toast.success('Senha copiada para a área de transferência');
  };

  const getPerfilLabel = (perfil) => {
    const found = perfis.find(p => p.value === perfil);
    return found?.label || perfil;
  };

  const getEmpresaNome = (id) => {
    const emp = empresas.find(e => e.id === id);
    return emp ? emp.nome_fantasia : '-';
  };

  const filteredUsuarios = usuarios.filter(u =>
    u.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="usuarios-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Usuários</h2>
          <p className="text-muted-foreground">Gerencie os usuários da plataforma</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="novo-usuario-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Criar Usuário</DialogTitle>
              <DialogDescription>Cadastre um novo usuário no sistema</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome Completo *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  required
                  data-testid="input-nome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  data-testid="input-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="perfil">Perfil *</Label>
                <Select
                  value={formData.perfil}
                  onValueChange={(value) => setFormData({...formData, perfil: value})}
                >
                  <SelectTrigger data-testid="select-perfil">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {perfis.map(perfil => (
                      <SelectItem key={perfil.value} value={perfil.value}>
                        {perfil.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="empresa">Empresa</Label>
                <Select
                  value={formData.empresa_id}
                  onValueChange={(value) => setFormData({...formData, empresa_id: value})}
                >
                  <SelectTrigger data-testid="select-empresa">
                    <SelectValue placeholder="Selecione (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {empresas.map(empresa => (
                      <SelectItem key={empresa.id} value={empresa.id}>
                        {empresa.nome_fantasia}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-usuario-btn">
                  Criar Usuário
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-100">
        <CardHeader className="pb-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar usuário..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-usuario"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredUsuarios.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhum usuário encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>E-mail</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Senha</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsuarios.map((usuario) => (
                    <TableRow key={usuario.id} data-testid={`usuario-row-${usuario.id}`}>
                      <TableCell className="font-medium">{usuario.nome}</TableCell>
                      <TableCell>{usuario.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={usuario.perfil === 'administrador_plataforma' ? 'border-purple-300 bg-purple-50 text-purple-700' : ''}>
                          {usuario.perfil === 'administrador_plataforma' && <Shield className="w-3 h-3 mr-1" />}
                          {getPerfilLabel(usuario.perfil)}
                        </Badge>
                      </TableCell>
                      <TableCell>{getEmpresaNome(usuario.empresa_id)}</TableCell>
                      <TableCell>
                        <Badge variant={usuario.ativo ? 'default' : 'secondary'} className={usuario.ativo ? 'bg-accent' : 'bg-red-100 text-red-700'}>
                          {usuario.ativo ? 'Ativo' : 'Bloqueado'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {usuario.forcar_troca_senha ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 text-xs">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Trocar
                            </Badge>
                          ) : usuario.senha_padrao ? (
                            <Badge variant="outline" className="text-slate-500 border-slate-300 text-xs">
                              <Key className="w-3 h-3 mr-1" />
                              Temp
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 text-xs">
                              <KeyRound className="w-3 h-3 mr-1" />
                              OK
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {usuario.perfil !== 'administrador_plataforma' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`usuario-actions-${usuario.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleResetarSenha(usuario)}>
                                <KeyRound className="w-4 h-4 mr-2" />
                                Resetar senha
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleForcarTrocaSenha(usuario)} className="text-amber-600">
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Forçar troca de senha
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {usuario.ativo ? (
                                <DropdownMenuItem onClick={() => handleBloquearUsuario(usuario, true)} className="text-red-600">
                                  <UserX className="w-4 h-4 mr-2" />
                                  Bloquear usuário
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleBloquearUsuario(usuario, false)} className="text-emerald-600">
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Desbloquear usuário
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDeleteClick(usuario)} 
                                className="text-destructive focus:text-destructive"
                                data-testid={`excluir-usuario-${usuario.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Excluir usuário
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para exibir senha temporária */}
      <Dialog open={senhaDialogOpen} onOpenChange={setSenhaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Senha Temporária Gerada</DialogTitle>
            <DialogDescription>
              Informe esta senha ao usuário. Ele deverá trocá-la no próximo login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">Usuário:</p>
              <p className="font-medium">{usuarioSelecionado?.nome}</p>
              <p className="text-sm text-slate-500">{usuarioSelecionado?.email}</p>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700 mb-2">Nova senha temporária:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-lg font-mono bg-white px-3 py-2 rounded border">
                  {senhaTemporaria}
                </code>
                <Button variant="outline" size="icon" onClick={copiarSenha}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              ⚠️ Esta senha será exibida apenas uma vez. Certifique-se de informá-la ao usuário.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setSenhaDialogOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Dialog para confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Excluir Usuário
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Você está prestes a excluir permanentemente o usuário:
              </p>
              {usuarioToDelete && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="font-semibold text-slate-900">{usuarioToDelete.nome}</p>
                  <p className="text-sm text-slate-500">{usuarioToDelete.email}</p>
                  <Badge variant="outline" className="mt-2">
                    {getPerfilLabel(usuarioToDelete.perfil)}
                  </Badge>
                </div>
              )}
              <p className="text-destructive font-semibold">
                Esta ação NÃO pode ser desfeita!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirmar-exclusao-usuario"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sim, Excluir
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsuariosPage;
